import { useLayoutEffect, useRef, type ReactNode } from "react";

/** The CSS variable the dice readout's `bottom` reads (`lib/dice/DiceOverlay.css`). */
export const DICE_READOUT_BOTTOM_VAR = "--lvtt-dice-readout-bottom";
/** The readout's own gap above whatever it clears, the same 32px it keeps off the viewport's bottom. */
const READOUT_GAP = 32;

/**
 * **THE ACTION ASKS**, docked at the map's bottom centre (`website-shell` 4.2, `decisions/0203` point
 * 8): the engine's question to this client, in a card rather than a floating window. The body is the
 * host's `PromptModal`.
 *
 * The app-wide dice readout (`DiceOverlay`) also sits at the bottom centre, above the whole app, and
 * its result card stays until dismissed, so it would cover the prompt's buttons. While the card is
 * docked it lifts the readout clear of itself through {@link DICE_READOUT_BOTTOM_VAR}, and puts it
 * back when it leaves.
 */
export function ActionAsksDock({ children }: { children: ReactNode }) {
  const ref = useRef<HTMLElement>(null);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const root = document.documentElement;
    const lift = () => {
      const top = el.getBoundingClientRect().top;
      root.style.setProperty(DICE_READOUT_BOTTOM_VAR, `${Math.max(READOUT_GAP, window.innerHeight - top + READOUT_GAP / 2)}px`);
    };
    lift();
    const ro = new ResizeObserver(lift);
    ro.observe(el);
    window.addEventListener("resize", lift);
    return () => {
      ro.disconnect();
      window.removeEventListener("resize", lift);
      root.style.removeProperty(DICE_READOUT_BOTTOM_VAR);
    };
  }, []);
  return (
    <section ref={ref} className="combat-asks" role="region" aria-label="The action asks">
      <div className="combat-asks-head">THE ACTION ASKS</div>
      {children}
    </section>
  );
}
